import { Colors } from '@kvika/audur-theme'
import { toPercentageString } from '@kvika/string-utils'
import { PrismicRichText, PrismicRichTextProps } from '@prismicio/react'
import { useEffect, useState } from 'react'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { Maybe } from '../../../prismic/prismicTypes'
import { ColumnClass, Gutters } from '../../../utils/types/Types'
import { GridItem, MainGridContainer } from '../../grid-container'

import styles from './interestRateCBChart.module.scss'
import { getInterestRateChartData } from './interestRateCBChartRequests'

interface Props {
	description?: Maybe<PrismicRichTextProps['field']>
	chartLine?: string
	chartTitle?: string
}

const InterestRateCBChart = ({ description, chartLine, chartTitle }: Props) => {
	const [chartLocalData, setChartLocalData] = useState<
		| {
				date: string
				value?: number
		  }[]
		| undefined
	>(undefined)

	useEffect(() => {
		const fetchChartData = async () => {
			getInterestRateChartData({
				from_date: '2021-01-01',
				interest_type: 'CentralBank7DayTermDeposits',
			})
				.then((data) => {
					const chartData =
						data?.rateRange
							.map((item) => ({
								date: item.activeFrom,
								value: item.interestRate * 100,
							}))
							.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) ?? []
					setChartLocalData(chartData)
				})
				.catch(() => {
					import('./interestRateCBChart.data').then((module) => {
						setChartLocalData(module.InterestRateCBChartData)
					})
				})
		}

		fetchChartData()
	}, [])

	if (!chartLocalData || chartLocalData.length === 0) {
		return null
	}

	return (
		<>
			<MainGridContainer gutters={Gutters.Both} backgroundColor={Colors.CardGray} className={styles.chartContainer}>
				<GridItem className={ColumnClass.All}>
					<div className={styles.description}>
						<PrismicRichText
							field={description as PrismicRichTextProps['field']}
							components={{
								hyperlink: ({ children, node }) => {
									const { url } = node.data
									return (
										<a
											href={url}
											target={'target' in node.data && node.data.target ? node.data.target : '_blank'}
											rel='noopener noreferrer'
											style={{
												color: Colors.Silver,
												textDecoration: 'underline',
												fontSize: '22px',
												lineHeight: '32px',
											}}
										>
											{children}
										</a>
									)
								},
							}}
						/>
					</div>
				</GridItem>
			</MainGridContainer>
			<MainGridContainer
				gutters={Gutters.Both}
				backgroundColor={Colors.CardGray}
				style={{ paddingBlock: '80px' }}
				className={styles.chartContainer}
			>
				<GridItem className={ColumnClass.All}>
					{chartTitle && <div className={styles.chartTitle}>{chartTitle}</div>}
					<ResponsiveContainer width='100%' height={400}>
						<LineChart data={chartLocalData} margin={{ top: 20, right: 0, left: 20, bottom: 10 }}>
							<CartesianGrid strokeDasharray='2' vertical={false} stroke={Colors.LightGray} />
							<XAxis
								dataKey='date'
								axisLine={{ strokeDasharray: '2', stroke: '#BEB8C4' }}
								tickLine={false}
								tick={{ fontSize: 12, color: '#DBDBDB', fill: '#DBDBDB', fontWeight: 400, opacity: 0.8, dy: 13 }}
								tickFormatter={(() => {
									let lastYear: number | null = null
									return (date) => {
										const year = new Date(date).getFullYear()
										if (year === lastYear) return '' // skip if repeated
										lastYear = year
										return year.toString()
									}
								})()}
							/>
							<Legend verticalAlign='top' align='right' />
							<YAxis
								axisLine={false}
								tickLine={false}
								tickCount={7}
								tick={{
									fontSize: 12,
									color: '#DBDBDB',
									fill: '#DBDBDB',
									opacity: 0.8,
									fontWeight: 400,
								}}
							/>
							<Line
								dataKey='value'
								name={chartLine || 'Stýrivextir'}
								fill={Colors.LightOrange}
								dot={false}
								stroke={Colors.LightOrange}
								strokeWidth={2}
							/>
							<Tooltip
								content={({ active, payload }) => {
									if (active && payload?.length) {
										const { date, value } = payload[0].payload
										if (typeof value === 'number' && typeof date === 'string') {
											const dateObj = new Date(date)
											const formattedDate = `${dateObj.getDate()}.${String(dateObj.getMonth() + 1).padStart(
												2,
												'0'
											)}.${dateObj.getFullYear()}`
											return (
												<div className={styles.tooltipContainer}>
													<p className={styles.percentage}>{toPercentageString(value, 2)}</p>
													<p className={styles.date}>{formattedDate}</p>
												</div>
											)
										}
									}
									return null
								}}
							/>
						</LineChart>
					</ResponsiveContainer>
				</GridItem>
			</MainGridContainer>
		</>
	)
}

export default InterestRateCBChart
